import { Download, Eye, FileText, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { fetchBlobUrl, get, post, postForm, toQuery } from '../api';
import { useAsk } from '../components/Ask';
import { Empty, ErrorBox, Loading, Spinner } from '../components/base';
import { formatDatePt, formatDateTimePt, formatEurosPt } from '../formats';

/**
 * Finance module: a product's ledger of costs and revenue, with documents,
 * and a profit-and-loss view by month.
 *
 * Three tabs:
 *   1. Entries — table of entries for the selected year/month, filterable by
 *      kind and category. Clicking an entry opens a detail panel with edit/delete
 *      actions and a document list with upload/view/delete.
 *   2. P&L — monthly table for the selected year: revenue, costs by category,
 *      result, and the document-status split.
 *   3. Revenue import — file upload for the CSV, with a preview table before
 *      commit. Shows the CSV format with a generic example.
 *
 * Drafted on the Forge (run 10) and corrected at the code gate, 2026-10-12:
 * uploads went through the JSON helper and arrived as "{}", the document list
 * and the edit form the design names were missing, and any error on a product
 * with no entries yet replaced the whole page, form included. After launch the
 * same day: the form used class names the stylesheet never defined, so it
 * rendered unstyled, and the P&L totals row left three columns blank.
 *
 * After the first browser test (2026-10-12): the App's own dialog in place of
 * the browser's prompts, the sum checked beside the Gross field before it is
 * sent, messages shown where the action was and cleared when the person moves
 * on, Portuguese money and dates, a Download beside the eye, and a duplicate
 * upload, a replaced import row and a declared-but-missing invoice all said
 * out loud rather than left to be noticed.
 */

type EntryKind = 'cost' | 'revenue';

interface EntryInput {
  date: string; kind: EntryKind; counterparty: string; description: string;
  category_key: string; net_cents: number; vat_cents: number; gross_cents: number;
  document_status?: DocumentStatus; note?: string | null;
}
type DocumentStatus = 'invoice' | 'receipt' | 'none';

interface FinanceEntry {
  id: string;
  date: string;
  kind: EntryKind;
  counterparty: string;
  description: string;
  category_key: string;
  net_cents: number;
  vat_cents: number;
  gross_cents: number;
  currency: string;
  document_status: DocumentStatus;
  entry_status: 'draft' | 'confirmed';
  source: string;
  note: string | null;
  changed_by_name: string | null;
  changed_at: string;
  created_at: string;
  has_documents: boolean;
  document_count: number;
}

interface FinanceDocument {
  id: string;
  entry_id: string;
  content_type: string;
  byte_size: number;
  original_filename: string;
  sha256: string;
  created_by_name: string | null;
  created_at: string;
}

interface PnLMonth {
  month: number;
  revenue_cents: number;
  costs_by_category: Array<{ key: string; label: string | null; cents: number }>;
  total_costs_cents: number;
  vat_on_revenue_cents: number;
  vat_on_costs_cents: number;
  result_cents: number;
  costs_with_invoice_cents: number;
  costs_with_receipt_cents: number;
  costs_without_document_cents: number;
}

interface PnLResponse {
  year: number;
  months: PnLMonth[];
  totals: {
    revenue_cents: number;
    total_costs_cents: number;
    vat_on_revenue_cents: number;
    vat_on_costs_cents: number;
    result_cents: number;
  };
}

interface CsvImportPreviewRow {
  date: string;
  channel: string;
  gross_cents: number;
  vat_cents: number;
  net_cents: number;
  status: 'new' | 'replaces';
  existing?: { gross_cents: number; vat_cents: number; net_cents: number; description: string };
}

interface CsvImportPreview {
  rows: CsvImportPreviewRow[];
  total_rows: number;
}

/** Format cents as euros. */
const centsToEuros = (cents: number) => (cents / 100).toFixed(2);

/** Human-readable category label from key: "office_rent" reads "Office rent". */
const humaniseKey = (key: string) => {
  const words = key.replace(/[-_]+/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const KIND_LABEL: Record<EntryKind, string> = { cost: 'Cost', revenue: 'Revenue' };
const DOCUMENT_LABEL: Record<DocumentStatus, string> = { invoice: 'Invoice', receipt: 'Receipt', none: 'None' };

/**
 * Who the money went to or came from. A cost has a supplier; revenue has a
 * channel - the shop, the website - and calling both "Counterparty" left the
 * founders asking which channel name to type.
 */
const partyLabel = (kind: EntryKind) => (kind === 'revenue' ? 'Channel' : 'Supplier');

/**
 * An entry that says it has an invoice or receipt with no file attached. The
 * P&L counts what an entry declares, so this is where a missing file shows.
 */
const missingFile = (entry: FinanceEntry) => entry.document_status !== 'none' && entry.document_count === 0;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default function Finance({ productId }: { productId: string }) {
  const [tab, setTab] = useState<'entries' | 'pnl' | 'import'>('entries');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState<number | undefined>(undefined);
  const [kindFilter, setKindFilter] = useState<string | undefined>(undefined);
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [entries, setEntries] = useState<FinanceEntry[]>([]);
  const [entriesTotal, setEntriesTotal] = useState(0);
  const [entriesPage, setEntriesPage] = useState(1);
  const [pnl, setPnl] = useState<PnLResponse | null>(null);
  const [preview, setPreview] = useState<CsvImportPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<FinanceEntry | null>(null);
  const [documents, setDocuments] = useState<FinanceDocument[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Where the last message belongs. One about the open entry - a refused
  // save, a duplicate upload - shows in its panel, which on a long list is
  // a screen below the top of the page where every message used to land.
  const [messageAt, setMessageAt] = useState<'page' | 'entry'>('page');
  const { ask } = useAsk();

  /** A fresh start for messages: the person has moved on from the last one. */
  function clearMessages(at: 'page' | 'entry' = 'page') {
    setError(null);
    setNotice(null);
    setMessageAt(at);
  }

  function openEntry(entry: FinanceEntry | null) {
    clearMessages();
    setSelectedEntry(entry);
  }

  const api = (path: string) => `/modules/${productId}/finance${path}`;

  // Load categories
  useEffect(() => {
    get<{ categories: Record<string, string> }>(api('/categories'))
      .then((data) => setCategories(data.categories))
      .catch(() => {});
  }, [productId]);

  // Load entries
  const loadEntries = useCallback(() => {
    get<{ entries: FinanceEntry[]; total: number }>(
      api(`/entries${toQuery({
        year, month, kind: kindFilter, page: entriesPage, limit: 50,
      })}`),
    ).then((data) => {
      setEntries(data.entries);
      setEntriesTotal(data.total);
      // The open panel holds its own copy of the row, taken when it was
      // clicked. Refresh it with the list, or an upload leaves the panel
      // saying "no file attached yet" under a list that says 1 (browser
      // test, 2026-10-12).
      setSelectedEntry((open) => (open ? data.entries.find((e) => e.id === open.id) ?? open : open));
    }).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load entries.'));
  }, [productId, year, month, kindFilter, entriesPage]);

  useEffect(() => {
    if (tab === 'entries') loadEntries();
  }, [tab, loadEntries]);

  // Load P&L
  const loadPnl = useCallback(() => {
    get<PnLResponse>(api(`/profit-and-loss${toQuery({ year })}`))
      .then(setPnl)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load P&L.'));
  }, [productId, year]);

  useEffect(() => {
    if (tab === 'pnl') loadPnl();
  }, [tab, loadPnl]);

  // The selected entry's documents, from their own route: the entry list
  // carries only a count.
  const loadDocuments = useCallback((entryId: string) => {
    get<{ documents: FinanceDocument[] }>(api(`/entries/${entryId}/documents`))
      .then((data) => setDocuments(data.documents))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load documents.'));
  }, [productId]);

  const selectedId = selectedEntry?.id;
  useEffect(() => {
    if (selectedId) loadDocuments(selectedId);
    else setDocuments([]);
  }, [selectedId, loadDocuments]);

  // -- Create and edit --------------------------------------------------------

  async function createEntry(data: EntryInput) {
    setBusy('create');
    clearMessages();
    try {
      await post(api('/entries'), data);
      setShowCreateForm(false);
      loadEntries();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create entry.');
    } finally {
      setBusy(null);
    }
  }

  /** True when saved, so the edit form closes only then and keeps what was typed otherwise. */
  async function updateEntry(entryId: string, data: EntryInput): Promise<boolean> {
    setBusy('update');
    clearMessages('entry');
    try {
      const updated = await post<FinanceEntry>(api(`/entries/${entryId}`), data);
      setSelectedEntry(updated);
      loadEntries();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save the entry.');
      return false;
    } finally {
      setBusy(null);
    }
  }

  // -- Delete entry -----------------------------------------------------------

  async function deleteEntry(entry: FinanceEntry, from: 'page' | 'entry') {
    const files = entry.document_count;
    const reason = await ask({
      title: `Delete this ${entry.kind} of ${formatEurosPt(entry.gross_cents)}?`,
      message: `${entry.counterparty}, ${formatDatePt(entry.date)}: ${entry.description}`
        + (files ? `\n\nIts ${files === 1 ? 'document is' : `${files} documents are`} deleted with it.` : '')
        + '\n\nThe audit trail keeps the figures and the reason'
        + `${files ? ", and each file's name and fingerprint, not the file itself" : ''}.`,
      field: { label: 'Why (kept in the audit trail)', required: true },
      confirmLabel: 'Delete entry',
      danger: true,
    });
    if (reason === null) return;
    setBusy(`delete-${entry.id}`);
    clearMessages(from);
    try {
      await post(api(`/entries/${entry.id}/delete`), { reason });
      setSelectedEntry(null);
      loadEntries();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete entry.');
    } finally {
      setBusy(null);
    }
  }

  // -- Upload document --------------------------------------------------------

  async function uploadDocument(entryId: string, file: File) {
    setBusy(`upload-${entryId}`);
    clearMessages('entry');
    try {
      const formData = new FormData();
      formData.append('document', file);
      const stored = await postForm<FinanceDocument & { duplicate: boolean }>(
        api(`/entries/${entryId}/documents`), formData);
      // The API keeps one copy of a file per entry and says when it already
      // had it. Silence here read as an upload that had vanished.
      if (stored.duplicate) {
        setNotice(`This entry already has that file, as "${stored.original_filename}". Nothing was added.`);
      }
      loadEntries();
      loadDocuments(entryId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to upload document.');
    } finally {
      setBusy(null);
    }
  }

  // -- Delete document --------------------------------------------------------

  async function deleteDocument(entryId: string, doc: FinanceDocument) {
    const reason = await ask({
      title: `Delete ${doc.original_filename}?`,
      message: "The file is deleted. The audit trail keeps its name, size and fingerprint, and the reason.",
      field: { label: 'Why (kept in the audit trail)', required: true },
      confirmLabel: 'Delete document',
      danger: true,
    });
    if (reason === null) return;
    const documentId = doc.id;
    setBusy(`doc-${documentId}`);
    clearMessages('entry');
    try {
      await post(api(`/entries/${entryId}/documents/${documentId}/delete`), { reason });
      loadEntries();
      loadDocuments(entryId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete document.');
    } finally {
      setBusy(null);
    }
  }

  // -- View document ----------------------------------------------------------

  /**
   * Open in a new tab, or save under its own name. Fetched with the token, so
   * the tab holds a blob: URL and its title is that URL's random id - the
   * "untitled" tab of the browser test. Download is the way to keep the file
   * with its name. Not shown inside the App: the CSP has no frame-src and
   * object-src 'none', deliberately (public/_headers), so a PDF cannot be
   * framed here without widening it for every frame.
   */
  async function openDocument(entryId: string, doc: FinanceDocument, save: boolean) {
    setBusy(`${save ? 'save' : 'view'}-${doc.id}`);
    clearMessages('entry');
    try {
      const url = await fetchBlobUrl(
        api(`/entries/${entryId}/documents/${doc.id}`),
        'Could not load the document.',
      );
      if (save) {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.original_filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, '_blank');
      }
      // The new tab or the download has its own copy once loaded; a minute is ample.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the document.');
    } finally {
      setBusy(null);
    }
  }

  // -- Export CSV -------------------------------------------------------------

  async function exportEntriesCsv() {
    setBusy('csv-entries');
    clearMessages();
    try {
      const url = await fetchBlobUrl(
        api(`/entries.csv${toQuery({ year, month })}`),
        'Could not build the file.',
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = `finance-entries-${year}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not build the file.');
    } finally {
      setBusy(null);
    }
  }

  async function exportPnlCsv() {
    setBusy('csv-pnl');
    clearMessages();
    try {
      const url = await fetchBlobUrl(
        api(`/profit-and-loss.csv${toQuery({ year })}`),
        'Could not build the file.',
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = `finance-pnl-${year}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not build the file.');
    } finally {
      setBusy(null);
    }
  }

  // -- Revenue CSV import -----------------------------------------------------

  async function handleCsvUpload(file: File, commit: boolean) {
    setBusy('csv-import');
    clearMessages();
    try {
      const formData = new FormData();
      formData.append('csv', file);
      const r = await postForm<CsvImportPreview | { rows_imported: number; rows_replaced: number }>(
        api(`/revenue/import${commit ? '?commit=true' : ''}`),
        formData,
      );
      if (!commit) {
        setCsvFile(file);
        setPreview(r as CsvImportPreview);
      } else {
        const done = r as { rows_imported: number; rows_replaced: number };
        setPreview(null);
        setCsvFile(null);
        setNotice(`Imported ${done.rows_imported} ${done.rows_imported === 1 ? 'row' : 'rows'}`
          + `${done.rows_replaced ? `, ${done.rows_replaced} replacing an earlier figure` : ''}.`);
        setTab('entries');
        loadEntries();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to import CSV.');
    } finally {
      setBusy(null);
    }
  }

  // -- Render -----------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div role="tablist" aria-label="Finance" className="flex flex-wrap gap-2 border-b border-paper-200 dark:border-ink-700">
        {(['entries', 'pnl', 'import'] as const).map((t) => (
          <button
            key={t} type="button" role="tab" aria-selected={tab === t}
            className={`px-4 py-2 text-sm font-medium transition border-b-2 -mb-px
              ${tab === t
                ? 'border-ink-900 text-ink-900 dark:border-paper-100 dark:text-paper-100'
                : 'border-transparent text-ink-500 hover:text-ink-700 dark:text-ink-400'}`}
            onClick={() => { clearMessages(); setTab(t); }}
          >
            {t === 'entries' ? 'Entries' : t === 'pnl' ? 'P&L' : 'Revenue import'}
          </button>
        ))}
      </div>

      {(messageAt === 'page' || !selectedEntry) && <Messages error={error} notice={notice} />}

      {/* Year selector. Not on the import tab: a file carries its own dates. */}
      {tab !== 'import' && (
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-sm text-ink-600 dark:text-paper-300">Year:</label>
        <select
          className="field w-auto"
          value={year}
          onChange={(e) => { setYear(Number(e.target.value)); setEntriesPage(1); }}
        >
          {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        {tab === 'entries' && (
          <>
            <label className="text-sm text-ink-600 dark:text-paper-300">Month:</label>
            <select
              className="field w-auto"
              value={month ?? ''}
              onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">All</option>
              {MONTHS.map((name, i) => (
                <option key={i + 1} value={i + 1}>{name}</option>
              ))}
            </select>
            <label className="text-sm text-ink-600 dark:text-paper-300">Kind:</label>
            <select
              className="field w-auto"
              value={kindFilter ?? ''}
              onChange={(e) => setKindFilter(e.target.value || undefined)}
            >
              <option value="">All</option>
              <option value="cost">Cost</option>
              <option value="revenue">Revenue</option>
            </select>
          </>
        )}
      </div>
      )}

      {/* Entries tab */}
      {tab === 'entries' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-ink-600 dark:text-paper-300">
              {entriesTotal} {entriesTotal === 1 ? 'entry' : 'entries'}
            </p>
            <div className="flex gap-2">
              <button type="button" className="btn-secondary text-xs" disabled={busy === 'csv-entries'}
                onClick={exportEntriesCsv}>
                {busy === 'csv-entries' ? <Spinner className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" aria-hidden />}
                Export CSV
              </button>
              <button type="button" className="btn-primary text-xs" onClick={() => {
                openEntry(null);
                setShowCreateForm(true);
              }}>
                <Plus className="h-3.5 w-3.5" aria-hidden /> New entry
              </button>
            </div>
          </div>

          {/* Create form */}
          {showCreateForm && (
            <EntryForm
              categories={categories}
              onSave={createEntry}
              onCancel={() => { clearMessages(); setShowCreateForm(false); }}
              busy={busy === 'create'}
            />
          )}

          {/* Entry list */}
          {entries.length === 0 ? (
            <div className="card">
              <Empty
                title="No entries"
                description="No entries for this period. Create one to get started."
              />
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-paper-200 dark:border-ink-700">
              <table className="min-w-full divide-y divide-paper-200 dark:divide-ink-700">
                <thead className="bg-paper-50 dark:bg-ink-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-ink-500">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-ink-500">Kind</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-ink-500">Supplier / channel</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-ink-500">Description</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-ink-500">Category</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">Gross</th>
                    <th className="px-4 py-2 text-center text-xs font-medium uppercase tracking-wide text-ink-500">Docs</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-paper-200 bg-white dark:divide-ink-700 dark:bg-ink-800">
                  {entries.map((entry) => (
                    <tr
                      key={entry.id}
                      className={`cursor-pointer transition hover:bg-paper-50 dark:hover:bg-ink-700
                        ${selectedEntry?.id === entry.id ? 'bg-sky-50 dark:bg-sky-900/20' : ''}`}
                      onClick={() => openEntry(selectedEntry?.id === entry.id ? null : entry)}
                    >
                      <td className="whitespace-nowrap px-4 py-2 text-sm">{formatDatePt(entry.date)}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm">
                        <span className={`badge ${entry.kind === 'cost' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'}`}>
                          {KIND_LABEL[entry.kind]}
                        </span>
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-2 text-sm">{entry.counterparty}</td>
                      <td className="max-w-[250px] truncate px-4 py-2 text-sm">{entry.description}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm">
                        {categories[entry.category_key] ?? humaniseKey(entry.category_key)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-medium">
                        {formatEurosPt(entry.gross_cents)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-center text-sm">
                        {entry.has_documents ? (
                          <span className="inline-flex items-center gap-1 text-xs text-ink-500">
                            <FileText className="h-3.5 w-3.5" aria-hidden />
                            {entry.document_count}
                          </span>
                        ) : missingFile(entry) ? (
                          <span className="badge bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            {DOCUMENT_LABEL[entry.document_status]}, no file
                          </span>
                        ) : (
                          <span className="text-xs text-ink-400">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        <button type="button" className="btn-secondary text-xs"
                          aria-label={`Delete the ${entry.kind} of ${formatDatePt(entry.date)}, ${entry.counterparty}`}
                          title="Delete"
                          onClick={(e) => { e.stopPropagation(); deleteEntry(entry, 'page'); }}>
                          <Trash2 className="h-3 w-3" aria-hidden />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Entry detail panel */}
          {selectedEntry && (
            <EntryDetail
              key={selectedEntry.id}
              entry={selectedEntry}
              documents={documents}
              categories={categories}
              onSave={(data) => updateEntry(selectedEntry.id, data)}
              onDelete={(entry) => deleteEntry(entry, 'entry')}
              onUploadDocument={uploadDocument}
              onDeleteDocument={deleteDocument}
              onOpenDocument={openDocument}
              onClearMessages={() => clearMessages('entry')}
              messages={messageAt === 'entry' ? <Messages error={error} notice={notice} /> : null}
              busy={busy}
            />
          )}

          {/* Pagination */}
          {entriesTotal > 50 && (
            <div className="flex items-center justify-between text-sm">
              <button type="button" className="btn-secondary text-xs" disabled={entriesPage === 1}
                onClick={() => setEntriesPage(entriesPage - 1)}>Previous</button>
              <span className="text-ink-500 dark:text-ink-400">
                Page {entriesPage} of {Math.ceil(entriesTotal / 50)}
              </span>
              <button type="button" className="btn-secondary text-xs" disabled={entriesPage * 50 >= entriesTotal}
                onClick={() => setEntriesPage(entriesPage + 1)}>Next</button>
            </div>
          )}
        </div>
      )}

      {/* P&L tab */}
      {tab === 'pnl' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-ink-600 dark:text-paper-300">
              Profit and loss for {year}
            </p>
            <button type="button" className="btn-secondary text-xs" disabled={busy === 'csv-pnl'}
              onClick={exportPnlCsv}>
              {busy === 'csv-pnl' ? <Spinner className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" aria-hidden />}
              Export CSV
            </button>
          </div>

          {!pnl ? <Loading /> : (
            <div className="overflow-x-auto rounded-xl border border-paper-200 dark:border-ink-700">
              <table className="min-w-full divide-y divide-paper-200 dark:divide-ink-700">
                <thead className="bg-paper-50 dark:bg-ink-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-ink-500">Month</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">Revenue</th>
                    {pnl.months[0]?.costs_by_category.map((cat) => (
                      <th key={cat.key} className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">
                        {cat.label ?? cat.key}
                      </th>
                    ))}
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">Total costs</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">VAT (rev)</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">VAT (costs)</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">Result</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">With invoice</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">With receipt</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">No doc</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-paper-200 bg-white dark:divide-ink-700 dark:bg-ink-800">
                  {pnl.months.map((m) => (
                    <tr key={m.month} className="hover:bg-paper-50 dark:hover:bg-ink-700">
                      <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">{MONTHS[m.month - 1]}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-sm">{formatEurosPt(m.revenue_cents)}</td>
                      {m.costs_by_category.map((cat) => (
                        <td key={cat.key} className="whitespace-nowrap px-4 py-2 text-right text-sm">
                          {formatEurosPt(cat.cents)}
                        </td>
                      ))}
                      <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-medium">{formatEurosPt(m.total_costs_cents)}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-sm">{formatEurosPt(m.vat_on_revenue_cents)}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-sm">{formatEurosPt(m.vat_on_costs_cents)}</td>
                      <td className={`whitespace-nowrap px-4 py-2 text-right text-sm font-bold ${m.result_cents >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatEurosPt(m.result_cents)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-sm">{formatEurosPt(m.costs_with_invoice_cents)}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-sm">{formatEurosPt(m.costs_with_receipt_cents)}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-sm">{formatEurosPt(m.costs_without_document_cents)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-paper-50 dark:bg-ink-800">
                  <tr>
                    <td className="px-4 py-2 text-sm font-bold">Total</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-bold">{formatEurosPt(pnl.totals.revenue_cents)}</td>
                    {pnl.months[0]?.costs_by_category.map((cat) => {
                      const total = pnl.months.reduce((sum, m) => {
                        const c = m.costs_by_category.find((cc) => cc.key === cat.key);
                        return sum + (c?.cents ?? 0);
                      }, 0);
                      return (
                        <td key={cat.key} className="whitespace-nowrap px-4 py-2 text-right text-sm font-bold">
                          {formatEurosPt(total)}
                        </td>
                      );
                    })}
                    <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-bold">{formatEurosPt(pnl.totals.total_costs_cents)}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-bold">{formatEurosPt(pnl.totals.vat_on_revenue_cents)}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-bold">{formatEurosPt(pnl.totals.vat_on_costs_cents)}</td>
                    <td className={`whitespace-nowrap px-4 py-2 text-right text-sm font-bold ${pnl.totals.result_cents >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatEurosPt(pnl.totals.result_cents)}
                    </td>
                    {([
                      'costs_with_invoice_cents', 'costs_with_receipt_cents', 'costs_without_document_cents',
                    ] as const).map((field) => (
                      <td key={field} className="whitespace-nowrap px-4 py-2 text-right text-sm font-bold">
                        {formatEurosPt(pnl.months.reduce((sum, m) => sum + m[field], 0))}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <p className="text-xs text-ink-500 dark:text-ink-400">
            With invoice and With receipt count what each cost entry says it has. An entry that says so with
            no file attached is marked in the Entries list.
          </p>
        </div>
      )}

      {/* Revenue import tab */}
      {tab === 'import' && (
        <div className="space-y-4">
          <div className="card">
            <h3 className="text-sm font-medium">Import revenue from CSV</h3>
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
              Upload a CSV file with daily revenue totals per channel. The file must have a header row
              and columns in any order. Either as Excel saves it in Portugal, with semicolons:
            </p>
            <pre className="mt-2 rounded-lg bg-paper-100 p-3 text-xs font-mono dark:bg-ink-700">
              date;channel;gross;vat;net{'\n'}
              15/01/2026;shop;1500,00;280,49;1219,51{'\n'}
              15/01/2026;online;2000,00;;1626,02
            </pre>
            <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">or with commas, and then a dot in the amounts:</p>
            <pre className="mt-2 rounded-lg bg-paper-100 p-3 text-xs font-mono dark:bg-ink-700">
              date,channel,gross,vat,net{'\n'}
              2026-01-15,shop,1500.00,280.49,1219.51
            </pre>
            <ul className="mt-2 space-y-1 text-xs text-ink-500 dark:text-ink-400">
              <li>• One row per day per channel</li>
              <li>• No thousands separator: 1500,00, not 1.500,00</li>
              <li>• Dates as 2026-01-15, or 15/01/2026 in a semicolon file</li>
              <li>• <code className="font-mono">gross</code> is required, plus at least one of <code className="font-mono">vat</code> or <code className="font-mono">net</code></li>
              <li>• A channel is one lowercase word of letters, digits, - or _, starting with a letter (e.g., shop, online, pop-up)</li>
              <li>• At most 1,000 rows per file</li>
            </ul>

            <div className="mt-4">
              <input
                type="file"
                accept=".csv"
                className="block w-full text-sm file:mr-4 file:rounded-lg file:border-0 file:bg-ink-900 file:px-4 file:py-2 file:text-sm file:text-paper-50 hover:file:bg-ink-700 dark:file:bg-paper-100 dark:file:text-ink-900"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleCsvUpload(file, false);
                  // So choosing the same file again, once corrected, is a change.
                  e.target.value = '';
                }}
              />
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Preview ({preview.total_rows} rows)</h3>
              {preview.rows.some((r) => r.status === 'replaces') && (
                <p className="text-xs text-ink-500 dark:text-ink-400">
                  A day and channel already in the ledger is replaced: its amounts change, and its description
                  stays as it was written.
                </p>
              )}
              <div className="overflow-x-auto rounded-xl border border-paper-200 dark:border-ink-700">
                <table className="min-w-full divide-y divide-paper-200 dark:divide-ink-700">
                  <thead className="bg-paper-50 dark:bg-ink-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-ink-500">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-ink-500">Channel</th>
                      <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">Gross</th>
                      <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">VAT</th>
                      <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-ink-500">Net</th>
                      <th className="px-4 py-2 text-center text-xs font-medium uppercase tracking-wide text-ink-500">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-ink-500">Replaces</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-paper-200 bg-white dark:divide-ink-700 dark:bg-ink-800">
                    {preview.rows.map((row, i) => (
                      <tr key={i} className="hover:bg-paper-50 dark:hover:bg-ink-700">
                        <td className="whitespace-nowrap px-4 py-2 text-sm">{formatDatePt(row.date)}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-sm">{row.channel}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-right text-sm">{formatEurosPt(row.gross_cents)}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-right text-sm">{formatEurosPt(row.vat_cents)}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-right text-sm">{formatEurosPt(row.net_cents)}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-center text-sm">
                          <span className={`badge ${row.status === 'new' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'}`}>
                            {row.status === 'new' ? 'New' : 'Replaces'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-ink-500 dark:text-ink-400">
                          {row.existing && (
                            <>
                              {formatEurosPt(row.existing.gross_cents)}
                              <span className="block text-xs">“{row.existing.description}” is kept</span>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button type="button" className="btn-primary text-sm" disabled={busy === 'csv-import'}
                onClick={() => { if (csvFile) handleCsvUpload(csvFile, true); }}>
                {busy === 'csv-import' ? <Spinner className="h-4 w-4" /> : <Upload className="h-4 w-4" aria-hidden />}
                Commit import
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -- Messages -----------------------------------------------------------------

function Messages({ error, notice }: { error: string | null; notice: string | null }) {
  return (
    <>
      {error && <ErrorBox message={error} />}
      {notice && (
        <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800
                     dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
          {notice}
        </p>
      )}
    </>
  );
}

// -- Entry form ---------------------------------------------------------------

function EntryForm({ categories, onSave, onCancel, busy, initial }: {
  categories: Record<string, string>;
  onSave: (data: EntryInput) => void;
  onCancel: () => void;
  busy: boolean;
  /** An entry to edit; absent for a new one. */
  initial?: FinanceEntry;
}) {
  const [sumError, setSumError] = useState<string | null>(null);
  const [kind, setKind] = useState<EntryKind>(initial?.kind ?? 'cost');
  const [date, setDate] = useState(initial?.date ?? new Date().toISOString().slice(0, 10));
  const [counterparty, setCounterparty] = useState(initial?.counterparty ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [categoryKey, setCategoryKey] = useState(
    initial && initial.kind === 'cost' ? initial.category_key : 'other');
  const [netCents, setNetCents] = useState(initial ? centsToEuros(initial.net_cents) : '');
  const [vatCents, setVatCents] = useState(initial ? centsToEuros(initial.vat_cents) : '');
  const [grossCents, setGrossCents] = useState(initial ? centsToEuros(initial.gross_cents) : '');
  const [documentStatus, setDocumentStatus] = useState<DocumentStatus>(initial?.document_status ?? 'none');
  const [note, setNote] = useState(initial?.note ?? '');
  // The create form and an edit form can be open at once, so ids are per form.
  const uid = useId();
  const id = (name: string) => `${uid}-${name}`;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const net = Math.round(parseFloat(netCents || '0') * 100);
    const vat = Math.round(parseFloat(vatCents || '0') * 100);
    const gross = Math.round(parseFloat(grossCents || '0') * 100);
    // The API refuses this too, but in a banner at the top of the page and
    // in its own words; the browser test met "net_cents + vat_cents must
    // equal gross_cents." there, a screen away from the figures it meant.
    if (net + vat !== gross) {
      setSumError(`Net plus VAT is ${formatEurosPt(net + vat)}, but Gross is ${formatEurosPt(gross)}.`
        + ' The three must add up.');
      return;
    }
    onSave({
      date, kind, counterparty, description,
      category_key: kind === 'revenue' ? 'revenue' : categoryKey,
      net_cents: net, vat_cents: vat, gross_cents: gross,
      document_status: kind === 'cost' ? documentStatus : undefined,
      // null clears a note on an edit; a new entry simply has none.
      note: note.trim() || (initial ? null : undefined),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{initial ? 'Edit entry' : 'New entry'}</h3>
        <button type="button" className="btn-secondary text-xs" onClick={onCancel}>Cancel</button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="form-label" htmlFor={id('kind')}>Kind</label>
          {/* Focus lands here when the form opens, which also scrolls it into
              view: a "New entry" click on a long page otherwise opened a form
              above the fold and looked like it had done nothing. */}
          <select id={id('kind')} className="field" value={kind} autoFocus
            onChange={(e) => setKind(e.target.value as EntryKind)}>
            <option value="cost">Cost</option>
            <option value="revenue">Revenue</option>
          </select>
        </div>
        <div>
          <label className="form-label" htmlFor={id('date')}>Date</label>
          <input type="date" id={id('date')} className="field" value={date} onChange={(e) => setDate(e.target.value)} required />
        </div>
        <div>
          <label className="form-label" htmlFor={id('counterparty')}>{partyLabel(kind)}</label>
          <input type="text" id={id('counterparty')} className="field" value={counterparty}
            onChange={(e) => setCounterparty(e.target.value)} required
            placeholder={kind === 'revenue' ? 'e.g. shop, online' : 'Who was paid'} />
        </div>
        <div>
          <label className="form-label" htmlFor={id('description')}>Description</label>
          <input type="text" id={id('description')} className="field" value={description}
            onChange={(e) => setDescription(e.target.value)} required />
        </div>
        {kind === 'cost' && (
          <div>
            <label className="form-label" htmlFor={id('category')}>Category</label>
            <select id={id('category')} className="field" value={categoryKey} onChange={(e) => setCategoryKey(e.target.value)}>
              {Object.entries(categories).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
              {/* A category since removed from the list stays selectable on
                  its own entries, which the API keeps rather than rewrites. */}
              {!(categoryKey in categories) && <option value={categoryKey}>{humaniseKey(categoryKey)}</option>}
            </select>
          </div>
        )}
        <div>
          <label className="form-label" htmlFor={id('net')}>Net (€)</label>
          <input type="number" step="0.01" min="0" id={id('net')} className="field" value={netCents}
            onChange={(e) => { setNetCents(e.target.value); setSumError(null); }} required />
        </div>
        <div>
          <label className="form-label" htmlFor={id('vat')}>VAT (€)</label>
          <input type="number" step="0.01" min="0" id={id('vat')} className="field" value={vatCents}
            onChange={(e) => { setVatCents(e.target.value); setSumError(null); }} required />
        </div>
        <div>
          <label className="form-label" htmlFor={id('gross')}>Gross (€)</label>
          <input type="number" step="0.01" min="0" id={id('gross')} className="field" value={grossCents}
            onChange={(e) => { setGrossCents(e.target.value); setSumError(null); }} required
            aria-invalid={sumError ? true : undefined} aria-describedby={sumError ? id('sum') : undefined} />
          {sumError && (
            <p id={id('sum')} role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">{sumError}</p>
          )}
        </div>
        {kind === 'cost' && (
          <div>
            <label className="form-label" htmlFor={id('document')}>Document</label>
            <select id={id('document')} className="field" value={documentStatus} onChange={(e) => setDocumentStatus(e.target.value as DocumentStatus)}>
              <option value="none">None</option>
              <option value="invoice">Invoice</option>
              <option value="receipt">Receipt</option>
            </select>
          </div>
        )}
        <div className="sm:col-span-2">
          <label className="form-label" htmlFor={id('note')}>Note (optional)</label>
          <textarea id={id('note')} className="field" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" className="btn-primary text-sm" disabled={busy}>
          {busy ? <Spinner className="h-4 w-4" /> : null}
          Save entry
        </button>
      </div>
    </form>
  );
}

// -- Entry detail panel -------------------------------------------------------

function EntryDetail({
  entry, documents, categories, onSave, onDelete, onUploadDocument, onDeleteDocument, onOpenDocument,
  onClearMessages, messages, busy,
}: {
  entry: FinanceEntry;
  documents: FinanceDocument[];
  categories: Record<string, string>;
  /** Resolves true once saved. */
  onSave: (data: EntryInput) => Promise<boolean>;
  onDelete: (entry: FinanceEntry) => void;
  onUploadDocument: (entryId: string, file: File) => void;
  onDeleteDocument: (entryId: string, doc: FinanceDocument) => void;
  onOpenDocument: (entryId: string, doc: FinanceDocument, save: boolean) => void;
  onClearMessages: () => void;
  /** This entry's error or notice, shown here rather than at the top of the page. */
  messages: ReactNode;
  busy: string | null;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    // The form closes only on a save that went through. It closed at once
    // before, so a refused save threw away everything that had been typed.
    return (
      <div className="space-y-3">
        {messages}
        <EntryForm categories={categories} initial={entry} busy={busy === 'update'}
          onCancel={() => { onClearMessages(); setEditing(false); }}
          onSave={async (data) => { if (await onSave(data)) setEditing(false); }} />
      </div>
    );
  }

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Entry details</h3>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary text-xs" onClick={() => { onClearMessages(); setEditing(true); }}>
            <Pencil className="h-3 w-3" aria-hidden /> Edit
          </button>
          <button type="button" className="btn-secondary text-xs text-red-600"
            onClick={() => onDelete(entry)}>
            <Trash2 className="h-3 w-3" aria-hidden /> Delete
          </button>
        </div>
      </div>

      {messages}

      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <div><dt className="text-xs uppercase tracking-wide text-ink-500">Date</dt>
          <dd>{formatDatePt(entry.date)}</dd></div>
        <div><dt className="text-xs uppercase tracking-wide text-ink-500">Kind</dt>
          <dd><span className={`badge ${entry.kind === 'cost' ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>{KIND_LABEL[entry.kind]}</span></dd></div>
        <div><dt className="text-xs uppercase tracking-wide text-ink-500">{partyLabel(entry.kind)}</dt>
          <dd>{entry.counterparty}</dd></div>
        <div><dt className="text-xs uppercase tracking-wide text-ink-500">Description</dt>
          <dd>{entry.description}</dd></div>
        <div><dt className="text-xs uppercase tracking-wide text-ink-500">Category</dt>
          <dd>{categories[entry.category_key] ?? humaniseKey(entry.category_key)}</dd></div>
        <div><dt className="text-xs uppercase tracking-wide text-ink-500">Net</dt>
          <dd>{formatEurosPt(entry.net_cents)}</dd></div>
        <div><dt className="text-xs uppercase tracking-wide text-ink-500">VAT</dt>
          <dd>{formatEurosPt(entry.vat_cents)}</dd></div>
        <div><dt className="text-xs uppercase tracking-wide text-ink-500">Gross</dt>
          <dd className="font-medium">{formatEurosPt(entry.gross_cents)}</dd></div>
        <div><dt className="text-xs uppercase tracking-wide text-ink-500">Document</dt>
          <dd>
            {DOCUMENT_LABEL[entry.document_status]}
            {missingFile(entry) && <span className="ml-2 text-xs text-amber-700 dark:text-amber-300">no file attached yet</span>}
          </dd></div>
        <div><dt className="text-xs uppercase tracking-wide text-ink-500">Source</dt>
          <dd className="font-mono text-xs">{entry.source}</dd></div>
        {entry.note && (
          <div className="sm:col-span-2"><dt className="text-xs uppercase tracking-wide text-ink-500">Note</dt>
            <dd className="whitespace-pre-wrap">{entry.note}</dd></div>
        )}
        <div><dt className="text-xs uppercase tracking-wide text-ink-500">Changed by</dt>
          <dd>{entry.changed_by_name ?? '—'}</dd></div>
        <div><dt className="text-xs uppercase tracking-wide text-ink-500">Changed at</dt>
          <dd>{formatDateTimePt(entry.changed_at)}</dd></div>
      </dl>

      {/* Documents */}
      <div className="border-t border-paper-200 pt-3 dark:border-ink-700">
        <h4 className="text-xs font-medium uppercase tracking-wide text-ink-500">Documents</h4>
        {documents.length ? (
          <ul className="mt-2 divide-y divide-paper-200 text-sm dark:divide-ink-700">
            {documents.map((doc) => (
              <li key={doc.id} className="flex items-center justify-between gap-3 py-1.5">
                <span className="min-w-0 truncate">
                  <FileText className="mr-1 inline h-3.5 w-3.5 text-ink-400" aria-hidden />
                  {doc.original_filename}
                  <span className="ml-2 text-xs text-ink-400">
                    {Math.max(1, Math.round(doc.byte_size / 1024))} KB · {doc.created_by_name ?? '—'} · {formatDateTimePt(doc.created_at)}
                  </span>
                </span>
                <span className="flex shrink-0 gap-1">
                  <button type="button" className="btn-secondary text-xs" disabled={busy === `view-${doc.id}`}
                    onClick={() => onOpenDocument(entry.id, doc, false)} aria-label={`Open ${doc.original_filename}`}
                    title="Open in a new tab">
                    <Eye className="h-3 w-3" aria-hidden />
                  </button>
                  <button type="button" className="btn-secondary text-xs" disabled={busy === `save-${doc.id}`}
                    onClick={() => onOpenDocument(entry.id, doc, true)} aria-label={`Download ${doc.original_filename}`}
                    title="Download">
                    <Download className="h-3 w-3" aria-hidden />
                  </button>
                  <button type="button" className="btn-secondary text-xs text-red-600" disabled={busy === `doc-${doc.id}`}
                    onClick={() => onDeleteDocument(entry.id, doc)} aria-label={`Delete ${doc.original_filename}`}
                    title="Delete">
                    <Trash2 className="h-3 w-3" aria-hidden />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-xs text-ink-400">No documents attached</p>
        )}

        <div className="mt-2">
          <label className="btn-secondary inline-flex cursor-pointer items-center gap-1 text-xs">
            <Upload className="h-3.5 w-3.5" aria-hidden />
            Upload document
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onUploadDocument(entry.id, file);
                e.target.value = '';
              }} />
          </label>
        </div>
      </div>
    </div>
  );
}
